/**
 * Shared axios instance for the MSTMS admin panel.
 *
 * Every `src/api/*` module imports {@link apiClient} from here, so this file is
 * the single place that configures cross-cutting request/response behavior:
 *
 *  - **Base URL** from `VITE_API_BASE_URL` (defaults to the local dev API).
 *  - **Auth**: attaches the `Bearer` access token from localStorage
 *    (`mstms_access_token`) to every request except the anonymous CSRF bootstrap.
 *  - **CSRF**: for mutating methods (POST/PUT/PATCH/DELETE) it fetches a fresh
 *    CSRF token and sets `X-CSRF-Token` before the request goes out.
 *  - **401 handling**: on an authenticated 401 (session expired), clears the
 *    stored tokens/user and redirects to the login page, guarded so concurrent
 *    failures don't trigger multiple redirects.
 *
 * The two exported symbols are {@link apiClient} (the configured instance) and
 * {@link resetSessionExpiredRedirectFlag} (re-arm the redirect after re-login).
 */
import axios, { InternalAxiosRequestConfig, isAxiosError } from 'axios';
import i18n from '../i18n';

/** API base URL; overridden by the `VITE_API_BASE_URL` env var, else the local dev default. */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';

/** Avoid redirect loops if multiple requests fail at once. */
let sessionExpiredRedirectScheduled = false;

/** Call after a successful login so a later session expiry can redirect again. */
export function resetSessionExpiredRedirectFlag() {
  sessionExpiredRedirectScheduled = false;
}

/**
 * Fetch a fresh CSRF token before each mutating request (login, wizard, hierarchy, etc.).
 * Not cached: server restarts and cache LRU can invalidate tokens while clients still hold old values.
 */
async function fetchCsrfToken(): Promise<string> {
  const { data } = await apiClient.get<{ success: boolean; data: { token: string } }>('/auth/csrf-token');
  return data?.data?.token ?? '';
}

/** True for HTTP methods that change server state and therefore require CSRF protection. */
function isMutatingMethod(method: string): boolean {
  return ['post', 'put', 'patch', 'delete'].includes(method);
}

/** Mutating calls (except CSRF bootstrap itself) must send X-CSRF-Token — same rule as /auth/*. */
function needsCsrfHeader(url: string, method: string): boolean {
  if (!isMutatingMethod(method)) return false;
  if (url.includes('/auth/csrf-token')) return false;
  return true;
}

/**
 * The configured axios instance imported by every API module. Tokens are sent
 * via headers (not cookies), so `withCredentials` is `false`.
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

// Request interceptor: inject CSRF token (mutations) and the Bearer auth header.
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const url = config.url ?? '';
    const method = (config.method ?? 'get').toLowerCase();

    // Localize API responses to the in-app language (DEF-001).
    config.headers = config.headers ?? {};
    config.headers['x-lang'] = i18n.language || 'en';

    if (needsCsrfHeader(url, method)) {
      const token = await fetchCsrfToken();
      config.headers = config.headers ?? {};
      config.headers['X-CSRF-Token'] = token;
    }

    // Attach the stored access token to every call except the unauthenticated CSRF bootstrap GET.
    const authToken = localStorage.getItem('mstms_access_token');
    const isAnonymousCsrfBootstrap =
      method === 'get' && (url === '/auth/csrf-token' || url.endsWith('/auth/csrf-token'));
    if (authToken && !isAnonymousCsrfBootstrap) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * In-flight refresh promise, shared so concurrent 401s trigger only ONE refresh
 * call (the rest await the same rotation). Resolves to the new access token, or
 * null when refresh isn't possible (no refresh token) or the server rejects it.
 */
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('mstms_refresh_token');
  if (!refreshToken) return null;
  try {
    // Goes through the request interceptor, so CSRF is attached automatically.
    const { data } = await apiClient.post<{ success: boolean; data: { access_token: string; refresh_token: string } }>(
      '/auth/refresh',
      { refresh_token: refreshToken },
    );
    const newAccess = data?.data?.access_token;
    const newRefresh = data?.data?.refresh_token;
    if (!newAccess) return null;
    localStorage.setItem('mstms_access_token', newAccess);
    if (newRefresh) localStorage.setItem('mstms_refresh_token', newRefresh);
    return newAccess;
  } catch {
    return null;
  }
}

// Response interceptor: on an authenticated 401 (expired access token), try ONE
// silent refresh with the stored 7-day refresh token and retry the original
// request. Only when refresh is impossible/rejected do we clear auth and redirect
// to login — so a routine 30-minute access-token expiry no longer kicks the user out.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!isAxiosError(error)) return Promise.reject(error);
    const status = error.response?.status;
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const url = String(original?.url ?? '');
    const headers = original?.headers;
    const hadAuth = !!(headers && (headers as Record<string, unknown>).Authorization);
    // Never try to refresh off the auth endpoints themselves.
    const isAuthPath =
      url.includes('/auth/login') || url.includes('/auth/csrf-token') || url.includes('/auth/refresh');

    if (status === 401 && hadAuth && !isAuthPath && original && !original._retry) {
      original._retry = true;
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        // Retry the original request; the request interceptor re-attaches the new
        // token (and a fresh CSRF token for mutations) from localStorage.
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }
    }

    // Refresh unavailable or rejected → clear auth and redirect to login (once).
    if (status === 401 && hadAuth && !isAuthPath && !sessionExpiredRedirectScheduled) {
      sessionExpiredRedirectScheduled = true;
      localStorage.removeItem('mstms_access_token');
      localStorage.removeItem('mstms_refresh_token');
      localStorage.removeItem('mstms_user');
      window.location.assign(`${import.meta.env.BASE_URL}login`);
    }

    return Promise.reject(error);
  },
);

